import { createContext, useContext, useEffect, useState, useCallback } from "react";

/**
 * Admin-controlled copy + platform settings provider.
 *
 * Usage:
 *   const t = useAdminCopy();
 *   <h1>{t("me.tabs.preferences", "Preferences")}</h1>
 *
 *   const { settings } = useAdminSettings();
 *   settings["platform.brand.primary_color"] // "#000000"
 *
 * If a key is missing from admin_copy, the fallback is used. This means
 * existing hardcoded UI keeps working during the gradual migration.
 */

const AdminCopyContext = createContext({
  copy: {},
  settings: {},
  loading: true,
  refresh: () => {},
});

const API = process.env.REACT_APP_BACKEND_URL;

export function AdminCopyProvider({ children }) {
  const [copy, setCopy] = useState({});
  const [settings, setSettings] = useState({});
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [copyRes, setsRes] = await Promise.all([
        fetch(`${API}/api/admin-copy`),
        fetch(`${API}/api/platform-settings`),
      ]);
      if (copyRes.ok) {
        const d = await copyRes.json();
        setCopy(d.copy || {});
      }
      if (setsRes.ok) {
        const d = await setsRes.json();
        setSettings(d.settings || {});
      }
    } catch (err) {
      console.warn("AdminCopy fetch failed (using fallbacks):", err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  return (
    <AdminCopyContext.Provider value={{ copy, settings, loading, refresh }}>
      {children}
    </AdminCopyContext.Provider>
  );
}

/**
 * Hook: returns a function `t(key, fallback)` that looks up the string.
 * Fallback is returned if the key is missing, so hardcoded values keep
 * working during gradual migration.
 */
export function useAdminCopy() {
  const { copy } = useContext(AdminCopyContext);
  return useCallback(
    (key, fallback = "") => {
      if (!key) return fallback;
      return copy[key] ?? fallback;
    },
    [copy]
  );
}

export function useAdminSettings() {
  const { settings, refresh, loading } = useContext(AdminCopyContext);
  return { settings, refresh, loading };
}

export function useAdminCopyRaw() {
  return useContext(AdminCopyContext);
}
